import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import { ClientType } from "../../../types/clientType";
import TextLink from "../../../components/TextLinks";
import InfoCard from "../../../components/InfoCard";
import ProfilePreview from "../../../components/UserInterface/ProfilePreview";
import ExternalLink from "../../../components/ExternalLink";
import { ClientFileType } from "../../../types/clientFileType";
import Modal from "../../Modal";
import { Text } from "../../Type";
import { formatAddress } from "../../../utils/formatAddress";
import styles from "./index.module.scss";
import UploadDocumentsModal from "../../ADP/UploadDocumentsModal";
import { useStoreContext } from "../../../contexts/StoreProvider";
import { relationshipOptions, typeOfBenefitOptions, labelFor } from "../../../utils/labelMap/LabelMap";
import GeneratePdfModal from "../../ADP/GeneratePdfModal";
import { isPostSigningStatus } from "../../../utils/status";

const GET_CLIENT_DOCS = gql`
  query GetDocumentName($id: ID!) {
    clientFile(id: $id) {
      client_form {
        id
      }
      client_signature {
        name
        url
      }
      justification {
        name
        url
      }
      letter_of_rationale {
        name
        url
      }
      quote {
        name
        url
      }
      confirmation_of_benefits {
        name
        url
      }
      other_supporting_documents {
        name
        url
      }
      other_supporting_documents_alt {
        name
        url
      }
      wet_signature {
        name
        url
      }
    }
  }
`;

const Profile = ({
  client,
  file,
  formAction,
}: {
  client?: ClientType;
  file?: ClientFileType;
  formAction?: string;
}) => {
  const { state: { user }, } = useStoreContext();
  const history = useHistory();
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data, refetch } = useQuery(GET_CLIENT_DOCS, {
    variables: {
      id: client?.client_files[0].id,
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const handleUpload = () => {
    refetch();
    setOpenUploadModal(false);
  };

  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const handleGenerate = () => {
    setOpenGenerateModal(false);
  };

  const isAdmin = user?.role.name === "Administrator";

  if (!client) return null;
  const {
    id,
    first_name,
    last_name,
    health_card,
    health_card_version,
    birth_date,
    sex,
    social_assistance,
    long_term_care_home,
    cep,
    address,
    agent,
    sales_reps,
    authorizers,
  } = client;

  const generatePdf = ["Progress", "Review", "Action", "PendingSignature"].includes(String(formAction));

  const readOnly = (isPostSigningStatus(String(formAction)) || (file?.locked_by && file?.locked_by.id !== user?.id));

  return (
    <>
      <div className={styles.container}>
        <div className={styles.item}>
          <InfoCard
            title="Personal Information"
            sections={[
              {
                content: [
                  {
                    label: "Preferred Name",
                    value: `${first_name} ${last_name}`,
                  },
                  { label: "Health Card Number", value: health_card || "-" },
                  { label: "Version", value: health_card_version || "-" },
                  {
                    label: "Date of Birth",
                    value: birth_date || "-",
                  },
                  { label: "Sex", value: sex || "-" },
                  {
                    label: "Social Assistance Benefits",
                    value: social_assistance && (labelFor(typeOfBenefitOptions, social_assistance)) || "-",
                  },
                  {
                    label: "Long-Term Care Home",
                    value: long_term_care_home || "-",
                  },
                  {
                    label: "CEP-enabled",
                    value: cep ? "Yes" : "No",
                  },
                ],
              },
            ]}
          />
          <InfoCard
            title="Contact Information"
            sections={[
              {
                content: [
                  {
                    label: "Phone Number",
                    value: address && address.phone ? address.phone : "-",
                  },
                  {
                    label: "Address",
                    value: address ? formatAddress(address) : "-",
                  },
                ],
              },
              {
                title: "Person who has signing authority",
                content: [
                  {
                    label: "Name",
                    value:
                       agent?.first_name || agent?.last_name ? `${agent?.first_name} ${agent?.last_name}` : "-",
                  },
                  {
                    label: "Phone",
                    value:
                      agent?.phone ? agent.phone : "-",
                  },
                  {
                    label: "Address",
                    value: agent
                      ? formatAddress(agent)
                      : "-",
                  },
                  {
                    label: "Relationship",
                    value: labelFor(relationshipOptions, agent?.relationship),
                  },
                ],
              },
            ]}
          />
        </div>
        <div className={styles.item}>
          <InfoCard
            title="Files"
            sections={[
              {
                content: [
                  {
                    label: "ADP Application",
                    value: generatePdf
                    ? (
                      <TextLink
                        onClick={() => setOpenGenerateModal(true)}
                      >
                        Generate Application PDF
                      </TextLink>
                    )
                  : (
                        <TextLink
                          to={`/client/${id}/form/${file?.client_form?.id}`}
                        >
                          {formAction !== "NotStarted" ? "View " : "Start "} Application
                        </TextLink>
                      )
                  },
                  {
                    label: "Client Signature",
                    value: data?.clientFile?.client_signature ? (
                      <ExternalLink
                        href={data?.clientFile?.client_signature?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.client_signature?.name}
                      </ExternalLink>
                    ) : (
                      "No document added"
                    ),
                  },
                  {
                    label: "Justification for funding Chart",
                    value: data?.clientFile?.justification ? (
                      <ExternalLink
                        href={data?.clientFile?.justification?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.justification?.name}
                      </ExternalLink>
                    ) : (
                      "No document added"
                    ),
                  },
                  {
                    label: "Letter of Rationale",
                    value: data?.clientFile?.letter_of_rationale ? (
                      <ExternalLink
                        href={data?.clientFile?.letter_of_rationale?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.letter_of_rationale?.name}
                      </ExternalLink>
                    ) : (
                      "No document added"
                    ),
                  },
                  {
                    label: "Vendor Quote",
                    value: data?.clientFile?.quote ? (
                      <ExternalLink
                        href={data?.clientFile?.quote?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.quote?.name}
                      </ExternalLink>
                    ) : (
                      "No document added"
                    ),
                  },
                  {
                    label: "Confirmation of Benefits",
                    value: data?.clientFile?.confirmation_of_benefits ? (
                      <ExternalLink
                        href={data?.clientFile?.confirmation_of_benefits?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.confirmation_of_benefits?.name}
                      </ExternalLink>
                    ) : (
                      "No document added"
                    ),
                  },
                  {
                    label: "Wet Signature Document",
                    value: data?.clientFile?.wet_signature ? (
                      <ExternalLink
                        href={data?.clientFile?.wet_signature?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.wet_signature?.name}
                      </ExternalLink>
                    ) : (
                      "No documents added"
                    ),
                  },
                  {
                    label: "Other Supporting Documents",
                    value: data?.clientFile?.other_supporting_documents ? (
                      <ExternalLink
                        href={data?.clientFile?.other_supporting_documents?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.other_supporting_documents?.name}
                      </ExternalLink>
                    ) : (
                      "No documents added"
                    ),
                  },
                  {
                    label: "Other Supporting Documents (Alt)",
                    value: data?.clientFile?.other_supporting_documents_alt ? (
                      <ExternalLink
                        href={data?.clientFile?.other_supporting_documents_alt?.url}
                        target="_blank"
                        className={styles.fileLink}
                      >
                        {data?.clientFile?.other_supporting_documents_alt?.name}
                      </ExternalLink>
                    ) : "No documents added"
                  }
                ],
              },
            ]}
            headingAction={{
              title: "Upload/View Documents",
              onClick: () => setOpenUploadModal(true),
              disabled: (readOnly && false),
            }}
          />
          <InfoCard
            title="Care Team"
            sections={[
              {
                content: [
                  {
                    label: "Authorizers",
                    value: authorizers.length ? (
                      <>
                        {authorizers.map((auth) => (
                          <div key={auth.id} className={styles.profileList}>
                            <Link
                              to={isAdmin ? (`/admin/user/${auth.id}`) : (
                                `/user/${auth.id}`
                              )}
                              className={styles.profileLink}
                            >
                              <ProfilePreview size="medium" user={auth} />
                            </Link>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <Text>
                          No Authorizers currently assigned to this client.
                        </Text>
                        <TextLink to={`/client/${client.id}/edit`}>
                          Assign Authorizer(s)
                        </TextLink>
                      </>
                    ),
                  },
                ],
              },
              {
                content: [
                  {
                    label: "Motion",
                    value: sales_reps.length ? (
                      <>
                        {sales_reps.map((auth) => (
                          <div key={auth.id} className={styles.profileList}>
                            <Link
                              to={isAdmin ? (`/admin/user/${auth.id}`) : (
                                `/user/${auth.id}`
                              )}
                              className={styles.profileLink}
                            >
                              <ProfilePreview size="medium" user={auth} />
                            </Link>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <Text>
                          No Motion team members currently assigned to this
                          client.
                        </Text>
                        <TextLink to={`/client/${client.id}/edit`}>
                          Assign Motion Team Member(s)
                        </TextLink>
                      </>
                    ),
                  },
                ],
              },
            ]}
            headingAction={isAdmin ? ({
              title: "Assign Care Team",
              onClick: () => { history.push(`/client/${client.id}/edit`) },
              disabled: readOnly,
            }) : undefined}
          />
        </div>
      </div>
      <Modal
        isOpen={openUploadModal}
        title="Upload Documents"
        onClose={handleUpload}
      >
        <UploadDocumentsModal
          clientFileId={file?.id || ""}
          setUploadModalOpen={handleUpload}
        />
      </Modal>
      <Modal
        isOpen={openGenerateModal}
        title="Generate PDF"
        onClose={handleGenerate}
        closeOnOverlay={false}
        >
        <GeneratePdfModal
          clientFormId={data?.clientFile?.client_form.id}
          setGenerateModalOpen={handleGenerate}
        />
      </Modal>
    </>
  );
};

export default Profile;
